import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Database, Workflow, Bot, Search, FlaskConical, Package,
  ShieldCheck, Building2, LayoutDashboard, type LucideIcon,
  Menu, X, ChevronsLeft, ChevronsRight, Sun, Moon, LogOut, UserCircle2, BookOpen,
  Users, HelpCircle, Settings, TrendingUp, Sparkles, Hexagon,
  Compass, Zap, Activity, MessageCircleQuestion, Blocks, UserCog, ListChecks, PackagePlus,
  Code2,
  ScanSearch,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useApp, type Workspace } from "@/lib/store";
import { hexToHsl, DEFAULT_ACCENT_HSL } from "@/lib/utils";
import { WizardLauncher } from "@/components/Wizard";
import { BrandFooter } from "@/components/BrandFooter";
import { MarketingNav } from "@/components/MarketingNav";
import { PlatformAssistant } from "@/components/PlatformAssistant";
import { BrandLogo, BrandMark } from "@/components/BrandLogo";
import { VerifyEmailBanner } from "@/components/VerifyEmailBanner";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { rawFetch } from "@/lib/api/client";
import { applyRouteSeo } from "@/lib/seo";

type NavItem = { to: string; label: string; Icon: LucideIcon; help: string; access?: "staff" | "composer" | "scaffold" };
const NAV: NavItem[] = [
  { to: "/dashboard", label: "Dashboard",  Icon: LayoutDashboard, help: "Live API health, recent agent runs, recent pipeline runs." },
  { to: "/workspaces",label: "Workspaces", Icon: Building2,       help: "Tenants and the workspaces inside them. Switch from the header dropdown." },
  { to: "/users",     label: "Users",      Icon: Users,           help: "Members, roles, invitations, plan & seats." },
  { to: "/catalog",   label: "Catalog",    Icon: Database,        help: "Data sources + datasets. Upload, S3, MCP servers, Confluence, web crawl, DBs." },
  { to: "/pipelines", label: "Pipelines",  Icon: Workflow,        help: "DAGs of processors: parse → chunk → embed → extract → graph write → govern." },
  { to: "/metadata-profiles", label: "Metadata Profiles", Icon: ScanSearch, help: "Versioned input descriptions, transient evidence checks and redacted runtime decisions for any supported modality." },
  { to: "/agents",    label: "Agents",     Icon: Bot,             help: "LangGraph agents grounded in your ontology + retrieval profile, with live traces. Industry templates available." },
  { to: "/studio",    label: "Studio",     Icon: Compass,         help: "Architecture Studio — describe a business problem; Ontoligent designs the ontology, retrieval, pipeline, agent, governance and evals, then deploys it." },
  { to: "/studio/composer", label: "Composer", Icon: Blocks,     help: "Server-driven wizard: pick industry, business areas, regulations and extensions, then preview the composed ontology before deploying." },
  { to: "/actions",   label: "Actions",    Icon: Zap,             help: "Closed-loop, governed write-back: approval-gated, dry-runnable, idempotent and reversible actions." },
  { to: "/events",    label: "Events",     Icon: Activity,        help: "Behavioral layer: signals → behavior graph → features → triggers that fire agents or actions." },
  { to: "/ontology",  label: "Ontology",   Icon: Hexagon,         help: "Versioned entity & relation schemas. Generate a draft from a domain description, then promote via Evals." },
  { to: "/ontology/term-sets", label: "Term sets", Icon: BookOpen, help: "Admin-approved vocabulary, immutable versions and deterministic ontology draft inputs." },
  { to: "/developer", label: "Developer portal", Icon: Code2, help: "Pinned group/BU bundles, reviewed integrations, scoped service accounts and SDK contract." },
  { to: "/retrieval", label: "Retrieval",  Icon: Search,          help: "Hybrid retrieval profiles: vector + BM25 + graph + reranker, permission-aware." },
  { to: "/evals",     label: "Evals",      Icon: FlaskConical,    help: "Datasets + regression gates for ontology, retrieval, and agent changes." },
  { to: "/packs",     label: "Packs",      Icon: Package,         help: "Signed, versioned domain packs (ontology + connectors + prompts + evals)." },
  { to: "/marketplace",label:"Marketplace", Icon: Sparkles,        help: "One-click industry starter packs — finance, tax, healthcare, legal." },
  { to: "/governance",label: "Governance", Icon: ShieldCheck,     help: "Lineage, audit log, cost ledger, ABAC access decisions." },
  { to: "/knowledge-health", label: "Knowledge Health", Icon: Activity, help: "Coverage, freshness, consistency & confidence scored into a knowledge-debt grade — the Datadog for your knowledge." },
  { to: "/settings",  label: "Settings",   Icon: Settings,        help: "Workspace settings: name, accent color, default LLM/retrieval profile, privacy." },
  { to: "/howto",     label: "How-to",     Icon: HelpCircle,      help: "Step-by-step guide for every action in the Studio. Searchable." },
  { to: "/howto/orb", label: "ORB guide",  Icon: Package,         help: "Review, sign, verify and hand off ontology bundles." },
  { to: "/docs",      label: "Docs",       Icon: BookOpen,        help: "Concepts, quickstart, API reference." },
  { to: "/qna",       label: "Q&A",        Icon: MessageCircleQuestion, help: "Board-level enterprise Q&A, answered against the real product. Grounds the platform assistant too." },
  { to: "/use-cases", label: "Use cases",  Icon: TrendingUp,      help: "Concrete professional scenarios + ROI math." },
  { to: "/admin/templates", label: "Admin: Templates", Icon: UserCog, help: "Platform marketplace lifecycle: draft, publish, retire, clone and compare pack versions. Staff only.", access: "staff" },
  { to: "/admin/composition-runs", label: "Composition Runs", Icon: ListChecks, help: "Run history for the selected workspace. Staff can audit across workspaces.", access: "composer" },
  { to: "/admin/scaffold", label: "ORB Scaffold", Icon: PackagePlus, help: "Generate the Enterprise implementation ZIP for the selected workspace's published ontology.", access: "scaffold" },
];

// The sidebar follows the product journey. Keep every route in exactly one
// section when adding a surface so the order remains predictable.
const NAV_SECTIONS = [
  { title: "Start", routes: ["/dashboard", "/studio"] },
  { title: "Build knowledge", routes: ["/catalog", "/ontology/term-sets", "/packs", "/marketplace", "/studio/composer", "/ontology"] },
  { title: "Assure", routes: ["/evals", "/governance", "/knowledge-health"] },
  { title: "Run", routes: ["/metadata-profiles", "/pipelines", "/retrieval", "/agents", "/actions", "/events"] },
  { title: "Deliver", routes: ["/developer"] },
  { title: "Manage", routes: ["/workspaces", "/users", "/settings"] },
  { title: "Learn", routes: ["/howto", "/docs", "/howto/orb", "/qna", "/use-cases"] },
  { title: "Administration", routes: ["/admin/templates", "/admin/composition-runs", "/admin/scaffold"] },
] as const;

// Routes that always render the clean marketing / pre-auth shell (no Studio
// sidebar), regardless of auth state.
const MARKETING_ROUTES = new Set([
  "/",
  "/login",
  "/signup",
  "/welcome",
  "/verify-email",
  "/reset-password",
  "/legal/privacy-policy",
  "/legal/terms-and-conditions",
]);

// Routes reachable WITHOUT authentication (no redirect to home). This is a
// superset of the marketing routes: the public site links to Use cases, the
// Docs and the How-to guide from its header and footer, so an anonymous
// visitor must be able to open them directly (and via deep link). Docs and
// Journey and ORB read published content from the shared site-content API. When
// authenticated, they still render inside the Studio shell because they are
// not marketing routes. Governance remains a workspace-scoped Studio surface.
const PUBLIC_ROUTES = new Set([
  ...MARKETING_ROUTES,
  "/use-cases",
  "/journey",
  "/docs",
  "/qna",
  "/howto",
  "/howto/orb",
  "/brief",
]);

export function Layout({ children }: { children: ReactNode }) {
  const {
    workspaceId, workspaces, setWorkspace, setWorkspaces,
    token, user, logout,
    theme, toggleTheme,
    sidebarOpen, setSidebarOpen,
    sidebarCollapsed, toggleSidebarCollapsed,
  } = useApp();

  const nav = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const isPublic = PUBLIC_ROUTES.has(path);
  const isMarketing = MARKETING_ROUTES.has(path);

  const workspacesQ = useQuery<{ results: Workspace[] }>({
    queryKey: ["layout-workspaces", token],
    queryFn: () => rawFetch("/iam/workspaces/?page_size=100"),
    enabled: !!token,
    staleTime: 60_000,
  });
  const [blockedWorkspace, setBlockedWorkspace] = useState<Workspace | null>(null);
  const [joinError, setJoinError] = useState("");

  useEffect(() => {
    if (workspacesQ.data?.results) setWorkspaces(workspacesQ.data.results);
  }, [setWorkspaces, workspacesQ.data]);

  // Auth guard: when unauthenticated on a protected route, return to public home.
  useEffect(() => {
    if (!token && !isPublic) nav({ to: "/" });
  }, [token, isPublic, nav]);

  // Auto-close mobile drawer on route change.
  useEffect(() => { setSidebarOpen(false); }, [path, setSidebarOpen]);

  // Apply the active workspace's accent colour app-wide on load and whenever
  // the workspace is switched, so tenant branding is consistent everywhere
  // (not just after editing it in Settings). Falls back to the platform accent.
  useEffect(() => {
    const ws = workspaces.find((w) => w.id === workspaceId);
    document.documentElement.style.setProperty(
      "--accent", ws?.accent_color ? hexToHsl(ws.accent_color) : DEFAULT_ACCENT_HSL,
    );
  }, [workspaceId, workspaces]);

  // Keep title/meta/OG tags in sync with the active route in this SPA.
  useEffect(() => {
    applyRouteSeo(path);
  }, [path]);

  // Public routes (login/signup) get a clean shell with just theme toggle.
  // The animated BrandFooter only appears here (marketing / pre-auth surfaces);
  // the authenticated Studio keeps the chrome minimal so the main canvas is dense.
  //
  // Render the clean/marketing shell for true marketing routes (any auth state)
  // and for any anonymous visitor on a publicly accessible route. Authenticated
  // users on non-marketing public routes (Use cases, Docs) keep the Studio shell.
  if (isMarketing || !token) {
    // Welcome is a full-bleed marketing page; login/signup are centered cards.
    const isWelcome = path === "/" || path === "/welcome";
    // Resource pages (Use cases, Docs, How-to) need a wide canvas for their
    // grids and side-by-side layouts; login/signup stay as narrow cards.
    const isWidePublic = path === "/use-cases" || path === "/docs" || path === "/qna" || path === "/howto" || path === "/howto/orb" || path === "/journey" || path === "/brief" || path === "/legal/privacy-policy" || path === "/legal/terms-and-conditions";
    // Auth screens (login / signup) keep the bare wordmark header; every other
    // public/marketing surface — Welcome, Journey, Use cases, Docs, How-to —
    // shares the single MarketingNav so the top menu is identical everywhere.
    const isAuthScreen = path === "/login" || path === "/signup";
    const containerCls = isWelcome
      ? "flex-1"
      : isWidePublic
        ? "mx-auto w-full max-w-7xl flex-1 p-6"
        : isAuthScreen
          ? "mx-auto w-full max-w-5xl flex-1 p-6"
          : "mx-auto w-full max-w-3xl flex-1 p-6";
    return (
      <div className="flex min-h-full flex-col bg-bg text-text">
        {isAuthScreen ? (
          <header className="flex items-center justify-between border-b border-border px-4 py-3">
            <Link to="/" className="inline-flex items-center">
              <BrandLogo className="h-7 w-auto" priority alt="Ontoligent" />
            </Link>
            <button className="btn-ghost" onClick={toggleTheme}
                    aria-label="Toggle theme" title="Toggle theme">
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </header>
        ) : (
          <MarketingNav />
        )}
        <div className={containerCls}>
          {children}
        </div>
        <BrandFooter />
      </div>
    );
  }

  const sideWidth = sidebarCollapsed ? "md:w-16" : "md:w-56";
  const activeWorkspace = workspaces.find((workspace) => workspace.id === workspaceId);
  const isStaff = Boolean(user?.is_staff || user?.is_superuser);
  const canCreateWorkspace = isStaff || workspaces.some((workspace) =>
    workspace.membership_role === "owner" || workspace.membership_role === "admin");
  function chooseWorkspace(id: string) {
    if (id === "__create__") { nav({ to: "/workspaces" }); return; }
    const selected = workspaces.find((workspace) => workspace.id === id);
    if (selected?.is_member === false && !isStaff) {
      setBlockedWorkspace(selected);
      setJoinError("");
      void rawFetch(`/iam/workspaces/${selected.id}/`).catch(() => undefined);
      return;
    }
    setBlockedWorkspace(null);
    setWorkspace(id || null);
  }
  async function joinWorkspace() {
    if (!blockedWorkspace) return;
    try {
      await rawFetch(`/iam/workspaces/${blockedWorkspace.id}/join/`, { method: "POST" });
      const refreshed = await workspacesQ.refetch();
      if (refreshed.data?.results) setWorkspaces(refreshed.data.results);
      setWorkspace(blockedWorkspace.id);
      setBlockedWorkspace(null);
      setJoinError("");
    } catch (error) {
      setJoinError(error instanceof Error ? error.message : "Could not join workspace.");
    }
  }
  const entitlements = new Set(activeWorkspace?.entitlements ?? []);
  const canSeeNavItem = (access?: "staff" | "composer" | "scaffold") => {
    if (!access) return true;
    if (isStaff) return true;
    if (access === "staff") return false;
    if (access === "composer") return entitlements.has("semantic_composer");
    return entitlements.has("enterprise_scaffold");
  };

  return (
    <div className="relative flex h-full flex-col md:flex-row">
      {/* --- Mobile backdrop --- */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden animate-fadeIn"
          onClick={() => setSidebarOpen(false)}
          aria-hidden
        />
      )}

      {/* --- Sidebar --- */}
      <aside
        className={[
          "fixed inset-y-0 left-0 z-40 flex flex-col border-r border-border bg-panel/95 px-2 py-3",
          "w-60 transition-transform duration-200 ease-out",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
          "md:static md:z-auto md:translate-x-0 md:bg-panel/60",
          sideWidth,
        ].join(" ")}
        aria-label="Primary"
      >
        <div className="mb-3 flex items-center justify-between px-2">
          {/* Full wordmark / workspace brand: always shown in the mobile
             drawer; on desktop it is hidden only when the sidebar is
             collapsed (md:hidden). */}
          <span className={sidebarCollapsed ? "md:hidden" : ""}>
            {(() => {
              const ws = workspaces.find((w) => w.id === workspaceId);
              if (ws?.logo_url) {
                return (
                  <span className="flex items-center gap-2 text-sm font-semibold tracking-wide">
                    <img src={ws.logo_url} alt=""
                         className="h-6 w-6 rounded object-contain" />
                    <span className="truncate">{ws.name}</span>
                  </span>
                );
              }
              return (
                <span className="inline-flex items-center">
                  {/* Small screens: compact icon (the wide wordmark is too
                     large); md+ shows the full wordmark. */}
                  <BrandMark className="h-7 w-7 md:hidden" alt="Ontoligent" />
                  <BrandLogo className="hidden h-6 w-auto md:block" alt="Ontoligent" />
                </span>
              );
            })()}
          </span>
          {/* Collapsed mark: desktop-only (the mobile drawer always shows the
             full brand above). */}
          {sidebarCollapsed && (() => {
            const ws = workspaces.find((w) => w.id === workspaceId);
            return ws?.logo_url ? (
              <img src={ws.logo_url} alt=""
                   className="hidden h-6 w-6 rounded object-contain md:block" />
            ) : (
              <BrandMark className="hidden h-7 w-7 md:block" alt="Ontoligent" />
            );
          })()}
          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={toggleSidebarCollapsed}
              className="btn-ghost hidden md:inline-flex"
              aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              title={sidebarCollapsed ? "Expand" : "Collapse"}
            >
              {sidebarCollapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
            </button>
            <button
              onClick={() => setSidebarOpen(false)}
              className="btn-ghost md:hidden"
              aria-label="Close menu"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto scroll-thin pr-1">
          {NAV_SECTIONS.map(({ title, routes }) => {
            const items = routes.map((route) => NAV.find((item) => item.to === route))
              .filter((item): item is NavItem => item !== undefined && canSeeNavItem(item.access));
            if (items.length === 0) return null;
            return <div key={title} className="space-y-0.5">
              <div className={["px-2 pb-1 pt-3 font-mono text-[9px] uppercase tracking-widest text-muted", sidebarCollapsed ? "md:sr-only" : ""].join(" ")}>{title}</div>
              {items.map(({ to, label, Icon, help }) => <Link
                key={to}
                to={to}
                activeOptions={{ exact: true }}
                title={help}
                className="nav-link"
                activeProps={{ className: "nav-link nav-link-active" }}
              >
                <Icon size={16} className="shrink-0" />
                <span className={["truncate", sidebarCollapsed ? "md:hidden" : ""].join(" ")}>{label}</span>
              </Link>)}
            </div>;
          })}
        </nav>

        <div className="mt-3 border-t border-border pt-2 px-2 space-y-1">
          <a
            href="/"
            target="_blank"
            rel="noopener"
            className="nav-link"
            title="Open the public marketing site in a new tab."
          >
            <BookOpen size={16} className="shrink-0" />
            <span className={sidebarCollapsed ? "md:hidden" : ""}>Marketing site</span>
          </a>
          {user && (
            <div className={["px-2 pt-2 text-[11px] leading-tight text-muted", sidebarCollapsed ? "md:hidden" : ""].join(" ")}>
              <div className="truncate text-text">{user.username}</div>
              <div className="truncate">{user.email}</div>
            </div>
          )}
        </div>
      </aside>

      {/* --- Main column --- */}
      <main className="flex h-full min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex items-center justify-between gap-2 border-b border-border bg-panel/40 px-3 py-2  ">
          <div className="flex min-w-0 items-center gap-2">
            <button
              className="btn-ghost md:hidden"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={18} />
            </button>
            <div className="truncate text-xs text-muted">
              {(() => {
                const ws = workspaces.find((w) => w.id === workspaceId);
                if (!ws) return workspacesQ.isLoading ? "Loading workspace…" : "No workspace selected";
                return (
                  <span className="inline-flex items-center gap-2">
                    {ws.logo_url
                      ? <img src={ws.logo_url} alt=""
                             className="h-5 w-5 rounded object-contain" />
                      : null}
                    <span className="text-text">{ws.name}</span>
                    {ws.organization_name && ws.organization_name !== ws.name && (
                      <>
                        <span className="text-muted">·</span>
                        <span className="text-muted">{ws.organization_name}</span>
                      </>
                    )}
                  </span>
                );
              })()}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {workspaces.length > 0 && (
              <select
                className="input hidden h-8 w-[200px] shrink-0 py-0 sm:block"
                value={workspaceId ?? ""}
                onChange={(e) => chooseWorkspace(e.target.value)}
                aria-label="Active workspace"
              >
                <option value="">— select workspace —</option>
                {workspaces.map((w) => (
                  <option key={w.id} value={w.id}>{w.name}{w.is_member === false ? " · Not joined" : ""}</option>
                ))}
                {canCreateWorkspace && <option value="__create__">+ Create workspace…</option>}
              </select>
            )}

            <button
              className="btn-ghost"
              onClick={toggleTheme}
              aria-label="Toggle theme"
              title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            <LanguageSwitcher />

            <WizardLauncher />

            {user ? (
              <div className="flex items-center gap-1">
                <span
                  className="hidden items-center gap-1 rounded-md border border-border bg-bg px-2 py-1 text-xs text-muted md:inline-flex"
                  title={user.email}
                >
                  <UserCircle2 size={14} className="text-accent" />
                  {user.username}
                </span>
                <button
                  className="btn-ghost"
                  onClick={() => { logout(); nav({ to: "/login" }); }}
                  aria-label="Sign out"
                  title="Sign out"
                >
                  <LogOut size={16} />
                </button>
              </div>
            ) : (
              <Link to="/login" className="btn">Sign in</Link>
            )}
          </div>
        </header>

        <VerifyEmailBanner />
        {blockedWorkspace && <div className="border-b border-warn/40 bg-warn/10 px-4 py-2 text-sm" role="alert">
          You are not a member of {blockedWorkspace.name}. Its content is unavailable until you join.
          <button className="btn ml-3" type="button" onClick={joinWorkspace}>Join as admin</button>
          <button className="btn-ghost ml-2" type="button" onClick={() => setBlockedWorkspace(null)}>Dismiss</button>
          {joinError && <span className="ml-2 text-bad">{joinError}</span>}
        </div>}
        <section className="flex-1 overflow-auto scroll-thin p-4 pb-20">
          {children}
        </section> 
      </main>
      <PlatformAssistant />
    </div>
  );
}
